import dayjs from "dayjs";
import { renderToString } from "react-dom/server";

import { dayAndShortMonth, yAxisLabelsConfig } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import dailyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/dailyIntervalConfig";
import bothAxisZoomConfig from "pro-features/components/Chart/src/configs/zoom/bothAxisZoomConfig";
import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import { getTooltipTitle } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/brands-visibility/utils";
import { VIEW_MODE_CONFIG } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/model-comparison/consts";
import { AI_MODELS_CONFIG } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import type { LLModel } from "../../../shared/types";

import { EViewMode } from "./types";
import type { SeriesItem } from "./types";

export const getGraphConfig = (data: Array<SeriesItem>, viewMode: EViewMode, availableModels: LLModel[]) => {
    const viewConfig = VIEW_MODE_CONFIG[viewMode];
    const yAxisFormatter = ({ value }) => percentageSignFilter()(value, 0);

    return combineConfigs({ yAxisFormatter }, [
        dailyIntervalConfig,
        bothAxisZoomConfig,
        {
            chart: {
                height: 300,
                marginRight: 16,
            },
            plotOptions: {
                column: {
                    pointPadding: 0.2,
                },
                series: {
                    minPointLength: 3,
                },
            },
            yAxis: {
                ...(viewConfig.yAxis.dataRange
                    ? {
                          ...viewConfig.yAxis.dataRange,
                          tickPositioner() {
                              const {
                                  dataRange: { min, max },
                                  tickInterval,
                              } = viewConfig.yAxis;

                              return Array.from(
                                  { length: Math.floor((max - min) / tickInterval) + 1 },
                                  (_, i) => min + i * tickInterval,
                              );
                          },
                      }
                    : {}),
                endOnTick: false,
                labels: {
                    formatter: function () {
                        return viewConfig.yAxis.formatter(this.value as number);
                    },
                },
            },
            xAxis: {
                labels: {
                    useHTML: true,
                    formatter: function () {
                        const model = availableModels?.[this.value];
                        const modelData = AI_MODELS_CONFIG?.[model];
                        const Icon = modelData?.Icon;

                        return renderToString(
                            <Flex
                                alignItems="center"
                                justifyContent="center"
                                gap="4px"
                            >
                                {<Icon size="xs" />}
                                <Text
                                    fontVariant="body2"
                                    color={colorsPalettes.carbon[400]}
                                >
                                    {translate(modelData?.displayNameKey)}
                                </Text>
                            </Flex>,
                        );
                    },
                },
            },
            tooltip: {
                outside: true,
                formatter() {
                    const model = availableModels?.[this.x];
                    const modelData = AI_MODELS_CONFIG?.[model];
                    const Icon = modelData?.Icon;
                    const rows = data
                        .filter((item) => item.visible && !item.isCoreAverage)
                        .map((rawData) => {
                            const point = rawData.data.find((p) => p[0] === this.x);

                            return {
                                displayName: rawData.name,
                                fieldValue: viewConfig.displayValueFilter(point?.[1]),
                                originalValue: point?.[1],
                                id: rawData.id,
                                isChecked: rawData.visible,
                                items: {
                                    label: rawData.name,
                                    color: rawData.color,
                                },
                            };
                        })
                        .sort((a, b) => b.originalValue - a.originalValue);

                    type TooltipRow = (typeof rows)[number];

                    const columnDefinition: ColumnDefinition<TooltipRow>[] = [
                        {
                            key: "items",
                            title: translate("llm.llm-comparison.graph.legend.header.brands"),
                            selector: "items" as const,
                            align: "left",
                            size: "150px",
                            render: ({ items }: TooltipRow) => (
                                <ChartTooltip.LabelCell
                                    label={items.label}
                                    backgroundColor={items.color}
                                />
                            ),
                        },
                        {
                            key: "fieldValue",
                            title: translate(viewConfig.displayNameKey),
                            selector: "fieldValue" as const,
                            align: "right",
                            size: "50px",
                            render: ({ fieldValue }: TooltipRow) => (
                                <ChartTooltip.ValueCell value={fieldValue.toString()} />
                            ),
                        },
                    ];

                    return renderToString(
                        <Flex width="220px">
                            <ChartTooltip
                                width={255}
                                title={
                                    <Flex
                                        alignItems="center"
                                        gap="8px"
                                    >
                                        {<Icon size="xs" />}
                                        <Text
                                            fontVariant="subtitle1"
                                            color={colorsPalettes.carbon[500]}
                                        >
                                            {translate(modelData?.displayNameKey)}
                                        </Text>
                                    </Flex>
                                }
                                columnDefinition={columnDefinition}
                                data={rows}
                            />
                        </Flex>,
                    );
                },
            },
        },
    ]);
};

export const getLineGraphConfig = (
    data: Array<SeriesItem>,
    granularity: GranularityNames,
    reportDuration: DatesRange,
) => {
    const viewConfig = VIEW_MODE_CONFIG[EViewMode.VisibilityOvertime];
    const yAxisFormatter = ({ value }) => percentageSignFilter()(value, 0);

    return combineConfigs({ yAxisFormatter }, [
        dailyIntervalConfig,
        yAxisLabelsConfig,
        bothAxisZoomConfig,
        {
            chart: {
                height: 300,
                marginRight: 16,
            },
            yAxis: {
                gridLineWidth: 0.5,
                showFirstLabel: true,
                tickLength: 20,
                startOnTick: true,
                minPadding: 0,
                min: 0,
                max: null,
            },
            xAxis: {
                type: "datetime",
                labels: {
                    formatter: function () {
                        return dayjs.utc(this.value).format(dayAndShortMonth);
                    },
                },
            },
            tooltip: {
                shared: true,
                outside: true,
                useHTML: true,
                formatter() {
                    const rows = data
                        .filter((item) => item.visible)
                        .map((rawData) => {
                            const point = rawData.data.find((p) => p[0] === this.x);

                            return {
                                displayName: rawData.name,
                                fieldValue: viewConfig.displayValueFilter(point?.[1] ?? 0),
                                originalValue: point?.[1] ?? 0,
                                id: rawData.id,
                                isChecked: rawData.visible,
                                items: {
                                    label: rawData.name,
                                    color: rawData.color,
                                },
                            };
                        })
                        .sort((a, b) => b.originalValue - a.originalValue);

                    type LineTooltipRow = (typeof rows)[number];

                    const columnDefinition: ColumnDefinition<LineTooltipRow>[] = [
                        {
                            key: "items",
                            title: translate("llm.llm-comparison.graph.legend.header.brands"),
                            selector: "items" as const,
                            align: "left",
                            size: "150px",
                            render: ({ items }: LineTooltipRow) => (
                                <ChartTooltip.LabelCell
                                    label={items.label}
                                    backgroundColor={items.color}
                                />
                            ),
                        },
                        {
                            key: "fieldValue",
                            title: translate(viewConfig.displayNameKey),
                            selector: "fieldValue" as const,
                            align: "right",
                            size: "50px",
                            render: ({ fieldValue }: LineTooltipRow) => (
                                <ChartTooltip.ValueCell value={fieldValue.toString()} />
                            ),
                        },
                    ];

                    const tooltipTitle = getTooltipTitle(dayjs.utc(this.key), granularity, reportDuration);
                    return renderToString(
                        <ChartTooltip
                            width={255}
                            title={tooltipTitle}
                            columnDefinition={columnDefinition}
                            data={rows}
                        />,
                    );
                },
            },
        },
    ]);
};
